import axios from 'axios';
import { api } from './authenticatedApi';

export class AuthService {
  private static readonly MAX_RETRIES = 3;
  private static readonly RETRY_DELAY = 1000;

  static async register(userData: {
    email: string;
    firstName: string;
    company?: string;
    password: string;
  }) {
    return this.withRetry(() => api.post('/auth/register', userData));
  }

  static async login(email: string, password: string) {
    return this.withRetry(() => api.post('/auth/login', { email, password }));
  }

  static async verify() {
    return api.get('/auth/verify');
  }

  static async getMe() {
    return api.get('/auth/me');
  }

  static async refreshToken(refreshToken: string) {
    return api.post('/auth/token/refresh', { refreshToken });
  }

  static async logout() {
    return api.post('/auth/logout');
  }

  static async requestPasswordReset(email: string) {
    return api.post('/auth/forgot-password', { email });
  }

  static async resetPassword(token: string, password: string) {
    return api.post('/auth/reset-password', { token, password });
  }

  static async validateResetToken(token: string) {
    return api.get(`/auth/validate-reset-token/${encodeURIComponent(token)}`);
  }

  static async updateProfile(updates: { firstName?: string; company?: string }) {
    return api.put('/auth/profile', updates);
  }

  static async changePassword(currentPassword: string, newPassword: string) {
    return api.post('/auth/change-password', { currentPassword, newPassword });
  }

  private static async withRetry<T>(operation: () => Promise<T>): Promise<T> {
    let lastError: Error;

    for (let attempt = 0; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        return await operation();
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (
          axios.isAxiosError(error) &&
          error.response?.status !== undefined &&
          error.response.status >= 400 &&
          error.response.status < 500 &&
          error.response.status !== 429
        ) {
          throw error;
        }

        if (attempt === this.MAX_RETRIES) {
          throw error;
        }

        const delay = this.RETRY_DELAY * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  }
}
